import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import type { SchemaRegion } from "../../../api/restaurantSchema";
import { SCHEMA_PAGE_PATH } from "../logic/schemaSetup";
import { isTableNameTaken, nextTableName } from "../../../components/restaurant-schema/tableNames";
import {
  PanelDivider,
  PanelField,
  PanelHeading,
  PanelInput,
  SeatsPicker,
  SegmentedSwitch,
} from "./tablesPanelControls";
import { PRIMARY_BUTTON } from "./tablesPanelStyles";
import { useTableNameError } from "./useTableNameDraft";
import { MAX_SERIES_TABLES, type TablesConfigActions } from "./useTablesConfigActions";

const DEFAULT_SEATS = 4;
// Колко номера предлага серията по подразбиране (от следващия свободен).
const DEFAULT_SERIES_LENGTH = 10;

type AddMode = "single" | "series";

/** Номерата от–до без заетите; грешка, ако диапазонът не става. */
const useSeriesPlan = (regions: SchemaRegion[], from: string, to: string) => {
  const { t } = useTranslation();
  return useMemo(() => {
    const isNumber = (value: string) => /^\d+$/.test(value.trim());
    if (!isNumber(from) || !isNumber(to) || Number(to) < Number(from)) {
      return { names: [], skippedCount: 0, error: t("tablesConfig.newTables.seriesInvalid") };
    }
    const total = Number(to) - Number(from) + 1;
    if (total > MAX_SERIES_TABLES) {
      return {
        names: [],
        skippedCount: 0,
        error: t("tablesConfig.newTables.seriesTooMany", { max: MAX_SERIES_TABLES }),
      };
    }
    const all = Array.from({ length: total }, (_, index) => String(Number(from) + index));
    const names = all.filter((name) => !isTableNameTaken(regions, name));
    return {
      names,
      skippedCount: all.length - names.length,
      error: names.length === 0 ? t("tablesConfig.newTables.seriesNothingFree") : null,
    };
  }, [regions, from, to, t]);
};

/**
 * „Нови маси" — панелът, когато нищо не е избрано: една маса (номерът
 * предлага следващия свободен, Enter добавя и фокусът остава за следващата)
 * или серия номера от–до (заетите се пропускат, до MAX_SERIES_TABLES наведнъж).
 */
const NewTablesPanel = ({
  region,
  regions,
  actions,
  canOpenSchema,
  focusRequest,
  onClose,
}: {
  region: SchemaRegion;
  regions: SchemaRegion[];
  actions: TablesConfigActions;
  // Линкът към „Схема на заведението" — само с правото за нея.
  canOpenSchema: boolean;
  // Всяка промяна фокусира номера („+ Нова маса" в мрежата).
  focusRequest: number;
  // На телефон панелът е в bottom sheet — там има „Затвори".
  onClose?: () => void;
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<AddMode>("single");
  // null = потребителят не е писал → показваме предложението (следва кеша).
  // Esc в поле само връща написаното; без написано стига до панела (затваря sheet-а).
  const [typedName, setTypedName] = useState<string | null>(null);
  const [typedFrom, setTypedFrom] = useState<string | null>(null);
  const [typedTo, setTypedTo] = useState<string | null>(null);
  const [seats, setSeats] = useState(DEFAULT_SEATS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const suggestedName = nextTableName(regions);
  const name = typedName ?? suggestedName;
  const nameError = useTableNameError(regions, name, null);
  const from = typedFrom ?? suggestedName;
  const to = typedTo ?? String(Number(suggestedName) + DEFAULT_SERIES_LENGTH - 1);
  const series = useSeriesPlan(regions, from, to);

  useEffect(() => {
    if (focusRequest === 0) return;
    setMode("single");
    // След смяната на режима полето вече е в DOM-а.
    const frame = requestAnimationFrame(() => nameInputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [focusRequest]);

  const canAddSingle = name.trim().length > 0 && !nameError && !isSubmitting;
  const canAddSeries = series.names.length > 0 && !series.error && !isSubmitting;

  const addSingle = async () => {
    if (!canAddSingle) return;
    setIsSubmitting(true);
    try {
      await actions.createTable(region, name.trim(), seats);
      setTypedName(null);
    } catch {
      // Съобщението е показано от useTablesConfigActions; написаното остава.
    } finally {
      setIsSubmitting(false);
      nameInputRef.current?.focus();
    }
  };

  const addSeries = async () => {
    if (!canAddSeries) return;
    setIsSubmitting(true);
    try {
      await actions.createTableSeries(region, series.names, seats);
      setTypedFrom(null);
      setTypedTo(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PanelHeading
        kicker={t("tablesConfig.newTables.kicker")}
        title={t("tablesConfig.newTables.title", { region: region.name })}
        onClose={onClose}
      />
      <SegmentedSwitch<AddMode>
        value={mode}
        onChange={setMode}
        options={[
          { value: "single", label: t("tablesConfig.newTables.single") },
          { value: "series", label: t("tablesConfig.newTables.series") },
        ]}
      />

      {mode === "single" ? (
        <PanelField label={t("tablesConfig.newTables.number")} error={nameError}>
          <PanelInput
            inputRef={nameInputRef}
            value={name}
            onChange={setTypedName}
            onEnter={() => void addSingle()}
            onEscape={typedName !== null ? () => setTypedName(null) : undefined}
            invalid={!!nameError}
            placeholder={t("restaurantSchema.table.numberPlaceholder")}
            ariaLabel={t("tablesConfig.newTables.number")}
          />
        </PanelField>
      ) : (
        <PanelField
          label={t("tablesConfig.newTables.numbers")}
          error={series.error}
          hint={
            series.skippedCount > 0
              ? t("tablesConfig.newTables.seriesSkippedHint", { count: series.skippedCount })
              : null
          }
        >
          <div className="grid grid-cols-2 gap-2.5">
            <PanelInput
              numeric
              value={from}
              onChange={setTypedFrom}
              onEnter={() => void addSeries()}
              onEscape={typedFrom !== null ? () => setTypedFrom(null) : undefined}
              invalid={!!series.error}
              placeholder={t("tablesConfig.newTables.from")}
              ariaLabel={t("tablesConfig.newTables.from")}
            />
            <PanelInput
              numeric
              value={to}
              onChange={setTypedTo}
              onEnter={() => void addSeries()}
              onEscape={typedTo !== null ? () => setTypedTo(null) : undefined}
              invalid={!!series.error}
              placeholder={t("tablesConfig.newTables.to")}
              ariaLabel={t("tablesConfig.newTables.to")}
            />
          </div>
        </PanelField>
      )}

      <PanelField label={t("tablesConfig.newTables.seats")}>
        <SeatsPicker value={seats} onChange={setSeats} />
      </PanelField>

      {mode === "single" ? (
        <button type="button" onClick={() => void addSingle()} disabled={!canAddSingle} className={PRIMARY_BUTTON}>
          <PlusOutlined className="text-[13px]" />
          {t("tablesConfig.newTables.add")}
        </button>
      ) : (
        <button type="button" onClick={() => void addSeries()} disabled={!canAddSeries} className={PRIMARY_BUTTON}>
          <PlusOutlined className="text-[13px]" />
          {t("tablesConfig.newTables.addMany", { count: series.names.length })}
        </button>
      )}

      <PanelDivider />
      <p className="m-0 text-[12.5px] leading-[1.55] text-body">
        <Trans
          i18nKey="tablesConfig.newTables.placementHint"
          components={{
            link: canOpenSchema ? (
              <Link
                to={SCHEMA_PAGE_PATH}
                className="font-semibold text-brand-deep-ink underline-offset-2 hover:underline"
              />
            ) : (
              <span className="font-semibold text-ink" />
            ),
          }}
        />
      </p>
    </div>
  );
};

export default NewTablesPanel;
